import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import {
  createClient,
  createFinancialAccount,
  createInvoice,
  createVendor,
  getFinanceOverview,
  getManagementPL,
  listBudgets,
  listClients,
  listDepartments,
  listFinancialAccounts,
  listFinancialTransactions,
  listInvoices,
  listProjects,
  listVendors,
  recordFinancialTransaction,
  setBudget,
  settleInvoice,
  type Budget,
  type Client,
  type Department,
  type FinancialAccount,
  type FinancialTransaction,
  type FinanceOverview,
  type Invoice,
  type ManagementPL,
  type Project,
  type Vendor,
} from '../api/client';
import { useLocalization } from '../context/LocalizationContext';
import { StatusBadge } from '../components/StatusBadge';
import { Icon } from '../components/Icon';

type Tab = 'overview' | 'transactions' | 'accounts' | 'invoices' | 'partners' | 'budgets' | 'pl';

export function Finance() {
  const { session, can } = useAuth();
  const { formatMoney, jurisdiction, country } = useLocalization();
  const [tab, setTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Data states
  const [overview, setOverview] = useState<FinanceOverview | null>(null);
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [transactions, setTransactions] = useState<FinancialTransaction[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [pl, setPL] = useState<ManagementPL | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  // Filter states
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<'all' | 'customer_invoice' | 'vendor_bill'>('all');
  const [txTypeFilter, setTxTypeFilter] = useState<string>('all');
  const [fiscalYear, setFiscalYear] = useState<string>('2026-2027');
  const [plFromDate, setPlFromDate] = useState<string>('2026-01-01');
  const [plToDate, setPlToDate] = useState<string>(new Date().toISOString().slice(0, 10));


  // Modal states
  const [showTxModal, setShowTxModal] = useState(false);
  const [showAccModal, setShowAccModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showSettleModal, setShowSettleModal] = useState<Invoice | null>(null);
  const [viewInvoiceModal, setViewInvoiceModal] = useState<Invoice | null>(null);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [attachedDocName, setAttachedDocName] = useState<string | null>(null);

  // Form states
  const [txForm, setTxForm] = useState({
    transactionType: 'expense' as const,
    transactionDate: new Date().toISOString().slice(0, 10),
    amount: '',
    accountId: '',
    departmentId: '',
    projectId: '',
    category: '',
    description: '',
    referenceNumber: '',
  });

  const [accForm, setAccForm] = useState({
    accountCode: '',
    accountName: '',
    type: 'bank' as const,
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    branchName: '',
    openingBalance: '',
  });

  const [vendorForm, setVendorForm] = useState({
    vendorCode: '',
    legalName: '',
    tradeName: '',
    category: 'software',
    gstin: '',
    pan: '',
    bankName: '',
    bankAccountNumber: '',
    bankIfsc: '',
    paymentTermsDays: '30',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
  });

  const [clientForm, setClientForm] = useState({
    clientCode: '',
    name: '',
    tradeName: '',
    gstin: '',
    pan: '',
    billingAddress: '',
    paymentTermsDays: '30',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
  });

  const [invForm, setInvForm] = useState<{
    invoiceNumber: string;
    invoiceType: 'customer_invoice' | 'vendor_bill';
    clientId: string;
    vendorId: string;
    projectId: string;
    issueDate: string;
    dueDate: string;
    subtotal: string;
    taxAmount: string;
    notes: string;
  }>({
    invoiceNumber: '',
    invoiceType: 'customer_invoice',
    clientId: '',
    vendorId: '',
    projectId: '',
    issueDate: new Date().toISOString().slice(0, 10),
    dueDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    subtotal: '',
    taxAmount: '0',
    notes: '',
  });


  const [settleForm, setSettleForm] = useState({
    amount: '',
    accountId: '',
    paymentReference: '',
  });

  const [budgetForm, setBudgetForm] = useState({
    fiscalYear: '2026-2027',
    departmentId: '',
    projectId: '',
    category: 'operational',
    allocatedAmount: '',
    alertThresholdPercent: '80',
  });

  async function loadAll() {
    setLoading(true);
    try {
      const [ovRes, accRes, txRes, venRes, cliRes, invRes, bgRes, plRes, depRes, prjRes] = await Promise.allSettled([
        getFinanceOverview(),
        listFinancialAccounts(),
        listFinancialTransactions({ limit: 100 }),
        listVendors(),
        listClients(),
        listInvoices(),
        listBudgets(fiscalYear),
        getManagementPL({ fromDate: plFromDate, toDate: plToDate }),
        listDepartments(),
        listProjects(),
      ]);

      if (ovRes.status === 'fulfilled') setOverview(ovRes.value.data);
      if (accRes.status === 'fulfilled') setAccounts(accRes.value.data);
      if (txRes.status === 'fulfilled') setTransactions(txRes.value.data);
      if (venRes.status === 'fulfilled') setVendors(venRes.value.data);
      if (cliRes.status === 'fulfilled') setClients(cliRes.value.data);
      if (invRes.status === 'fulfilled') setInvoices(invRes.value.data);
      if (bgRes.status === 'fulfilled') setBudgets(bgRes.value.data);
      if (plRes.status === 'fulfilled') setPL(plRes.value.data);
      if (depRes.status === 'fulfilled') setDepartments(depRes.value.items);
      if (prjRes.status === 'fulfilled') setProjects(prjRes.value.data);
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
  }, [fiscalYear, plFromDate, plToDate]);

  async function handleRecordTx(e: React.FormEvent) {
    e.preventDefault();
    if (!txForm.accountId || !txForm.amount || !txForm.category) return;
    try {
      await recordFinancialTransaction({
        organizationId: accounts[0]?.organization_id || '',
        transactionType: txForm.transactionType,
        transactionDate: txForm.transactionDate,
        amount: Number(txForm.amount),
        accountId: txForm.accountId,
        departmentId: txForm.departmentId || null,
        projectId: txForm.projectId || null,
        category: txForm.category,
        description: txForm.description || null,
        referenceNumber: txForm.referenceNumber || null,
      });
      setMsg({ type: 'success', text: 'Transaction recorded and ledger updated successfully!' });
      setShowTxModal(false);
      setTxForm({
        transactionType: 'expense',
        transactionDate: new Date().toISOString().slice(0, 10),
        amount: '',
        accountId: '',
        departmentId: '',
        projectId: '',
        category: '',
        description: '',
        referenceNumber: '',
      });
      await loadAll();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to record transaction' });
    }
  }

  async function handleCreateAccount(e: React.FormEvent) {
    e.preventDefault();
    if (!accForm.accountCode || !accForm.accountName) return;
    try {
      await createFinancialAccount({
        organizationId: accounts[0]?.organization_id || departments[0]?.id || '',
        accountCode: accForm.accountCode.toUpperCase(),
        accountName: accForm.accountName,
        type: accForm.type,
        bankName: accForm.bankName || null,
        accountNumber: accForm.accountNumber || null,
        ifscCode: accForm.ifscCode || null,
        branchName: accForm.branchName || null,
        openingBalance: Number(accForm.openingBalance || 0),
      });
      setMsg({ type: 'success', text: `Account ${accForm.accountCode} created successfully!` });
      setShowAccModal(false);
      setAccForm({
        accountCode: '',
        accountName: '',
        type: 'bank',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        branchName: '',
        openingBalance: '',
      });
      await loadAll();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to create account' });
    }
  }

  async function handleCreateVendor(e: React.FormEvent) {
    e.preventDefault();
    if (!vendorForm.vendorCode || !vendorForm.legalName) return;
    try {
      await createVendor({
        organizationId: accounts[0]?.organization_id || '',
        vendorCode: vendorForm.vendorCode.toUpperCase(),
        legalName: vendorForm.legalName,
        tradeName: vendorForm.tradeName || null,
        category: vendorForm.category,
        gstin: vendorForm.gstin || null,
        pan: vendorForm.pan || null,
        bankName: vendorForm.bankName || null,
        bankAccountNumber: vendorForm.bankAccountNumber || null,
        bankIfsc: vendorForm.bankIfsc || null,
        paymentTermsDays: Number(vendorForm.paymentTermsDays || 30),
        contactPerson: vendorForm.contactPerson || null,
        contactEmail: vendorForm.contactEmail || null,
        contactPhone: vendorForm.contactPhone || null,
      });
      setMsg({ type: 'success', text: `Vendor ${vendorForm.legalName} registered!` });
      setShowVendorModal(false);
      await loadAll();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to create vendor' });
    }
  }

  async function handleCreateClient(e: React.FormEvent) {
    e.preventDefault();
    if (!clientForm.clientCode || !clientForm.name) return;
    try {
      await createClient({
        organizationId: accounts[0]?.organization_id || '',
        clientCode: clientForm.clientCode.toUpperCase(),
        name: clientForm.name,
        tradeName: clientForm.tradeName || null,
        gstin: clientForm.gstin || null,
        pan: clientForm.pan || null,
        billingAddress: clientForm.billingAddress || null,
        paymentTermsDays: Number(clientForm.paymentTermsDays || 30),
        contactPerson: clientForm.contactPerson || null,
        contactEmail: clientForm.contactEmail || null,
        contactPhone: clientForm.contactPhone || null,
      });
      setMsg({ type: 'success', text: `Client ${clientForm.name} registered!` });
      setShowClientModal(false);
      await loadAll();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to create client' });
    }
  }

  async function handleCreateInvoice(e: React.FormEvent) {
    e.preventDefault();
    if (!invForm.invoiceNumber || !invForm.subtotal) return;
    try {
      await createInvoice({
        organizationId: accounts[0]?.organization_id || '',
        invoiceNumber: invForm.invoiceNumber.toUpperCase(),
        invoiceType: invForm.invoiceType,
        clientId: invForm.clientId || null,
        vendorId: invForm.vendorId || null,
        projectId: invForm.projectId || null,
        issueDate: invForm.issueDate,
        dueDate: invForm.dueDate,
        subtotal: Number(invForm.subtotal),
        taxAmount: Number(invForm.taxAmount || 0),
        notes: invForm.notes || null,
      });
      setMsg({ type: 'success', text: `Invoice/Bill #${invForm.invoiceNumber} issued!` });
      setShowInvoiceModal(false);
      await loadAll();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to create invoice' });
    }
  }

  async function handleSettleInvoice(e: React.FormEvent) {
    e.preventDefault();
    if (!showSettleModal || !settleForm.amount || !settleForm.accountId) return;
    try {
      await settleInvoice(showSettleModal.id, {
        amount: Number(settleForm.amount),
        accountId: settleForm.accountId,
        paymentReference: settleForm.paymentReference || null,
      });
      setMsg({ type: 'success', text: `Payment of ${formatMoney(Number(settleForm.amount))} settled for #${showSettleModal.invoice_number}` });
      setShowSettleModal(null);
      await loadAll();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to settle payment' });
    }
  }


  async function handleSetBudget(e: React.FormEvent) {
    e.preventDefault();
    if (!budgetForm.allocatedAmount) return;
    try {
      await setBudget({
        fiscalYear: budgetForm.fiscalYear,
        departmentId: budgetForm.departmentId || null,
        projectId: budgetForm.projectId || null,
        category: budgetForm.category,
        allocatedAmount: Number(budgetForm.allocatedAmount),
        alertThresholdPercent: Number(budgetForm.alertThresholdPercent || 80),
      });
      setMsg({ type: 'success', text: 'Budget allocation updated successfully!' });
      setShowBudgetModal(false);
      await loadAll();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to set budget' });
    }
  }

  if (loading) {
    return (
      <div className="panel" style={{ padding: 40, textAlign: 'center' }}>
        <div className="empty">Loading Enterprise Financial Control & Ledgers...</div>
      </div>
    );
  }

  const filteredInvoices = invoices.filter((inv) => {
    if (invoiceTypeFilter === 'all') return true;
    return inv.invoice_type === invoiceTypeFilter;
  });

  const filteredTransactions = transactions.filter((t) => {
    if (txTypeFilter === 'all') return true;
    return t.transaction_type === txTypeFilter;
  });

  return (
    <div>
      {/* Page Header */}
      <div className="page-head">
        <div>
          <span className="eyebrow">Financial Control & General Ledger</span>
          <h1>Enterprise Treasury, AP/AR & Cost Control</h1>
          <div className="muted mono" style={{ fontSize: 12, marginTop: 3 }}>
            Operating Entity: <strong>{session?.tenant.name || jurisdiction.sampleOrgName}</strong> · Accounting: <strong>{jurisdiction.accountingStandard}</strong> · Currency: <strong>{jurisdiction.currency} ({jurisdiction.currencySymbol})</strong>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          {can('finance.manage') && (
            <>
              <button className="btn-quiet btn-sm" onClick={() => setShowAccModal(true)}>
                <Icon name="plus" size="xs" />
                <span>New Account</span>
              </button>
              <button className="btn-primary btn-sm" onClick={() => setShowTxModal(true)}>
                <Icon name="plus" size="xs" />
                <span>Record Transaction</span>
              </button>
            </>
          )}
        </div>
      </div>

      {msg && <div className={`banner ${msg.type}`}>{msg.text}</div>}

      {/* Modern High-Density Navigation Tabs */}
      <div className="tabs" style={{ marginBottom: 20, display: 'flex', gap: 6, borderBottom: '1px solid var(--border)' }}>
        <button className={tab === 'overview' ? 'tab-btn active' : 'tab-btn'} onClick={() => setTab('overview')}>
          Overview & Cash
        </button>
        <button className={tab === 'transactions' ? 'tab-btn active' : 'tab-btn'} onClick={() => setTab('transactions')}>
          General Ledger ({transactions.length})
        </button>
        <button className={tab === 'accounts' ? 'tab-btn active' : 'tab-btn'} onClick={() => setTab('accounts')}>
          Bank & Cash Accounts ({accounts.length})
        </button>
        <button className={tab === 'invoices' ? 'tab-btn active' : 'tab-btn'} onClick={() => setTab('invoices')}>
          Payables (AP) & Receivables (AR) ({invoices.length})
        </button>
        <button className={tab === 'partners' ? 'tab-btn active' : 'tab-btn'} onClick={() => setTab('partners')}>
          Vendors & Clients
        </button>
        <button className={tab === 'budgets' ? 'tab-btn active' : 'tab-btn'} onClick={() => setTab('budgets')}>
          Budgets & Cost Centers
        </button>
        <button className={tab === 'pl' ? 'tab-btn active' : 'tab-btn'} onClick={() => setTab('pl')}>
          Management P&L
        </button>
      </div>

      {/* ---------------- 1. OVERVIEW & CASH CONTROL TAB ---------------- */}
      {tab === 'overview' && overview && (
        <div>
          {/* Top KPI Cards */}
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <div className="stat-card">
              <span className="stat-label">Total Liquid Reserves</span>
              <span className="stat-value money" style={{ color: 'var(--verified)' }}>
                {formatMoney(overview.balances.totalLiquidFunds)}
              </span>
              <span className="stat-hint">
                {formatMoney(overview.balances.totalBank)} in Bank · {formatMoney(overview.balances.totalCash)} Cash
              </span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Accounts Receivable (AR)</span>
              <span className="stat-value money" style={{ color: 'var(--ink)' }}>
                {formatMoney(overview.receivables.totalReceivables)}
              </span>
              <span className="stat-hint">
                {overview.receivables.openInvoicesCount} open invoices · <span style={{ color: overview.receivables.receivablesOverdue > 0 ? 'var(--failed)' : 'inherit' }}>{formatMoney(overview.receivables.receivablesOverdue)} overdue</span>
              </span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Accounts Payable (AP)</span>
              <span className="stat-value money" style={{ color: overview.payables.totalPayables > 0 ? 'var(--pending)' : 'inherit' }}>
                {formatMoney(overview.payables.totalPayables)}
              </span>
              <span className="stat-hint">
                {overview.payables.openBillsCount} vendor bills · <span style={{ color: overview.payables.payablesOverdue > 0 ? 'var(--failed)' : 'inherit' }}>{formatMoney(overview.payables.payablesOverdue)} overdue</span>
              </span>
            </div>

            <div className="stat-card">
              <span className="stat-label">MTD Net Cash Flow</span>
              <span className="stat-value money" style={{ color: overview.mtd.netCashFlow >= 0 ? 'var(--verified)' : 'var(--failed)' }}>
                {formatMoney(overview.mtd.netCashFlow)}
              </span>
              <span className="stat-hint">
                +{formatMoney(overview.mtd.income)} Inflow / -{formatMoney(overview.mtd.expenses)} Outflow
              </span>
            </div>
          </div>


          {/* Accounts Grid + Recent Transactions Split */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 20, marginTop: 20 }}>
            {/* Liquid Accounts Register */}
            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <span className="eyebrow">Bank & Cash Masters</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Active Financial Accounts</h3>
                </div>
                <button className="btn-quiet btn-sm" onClick={() => setShowAccModal(true)}>+ Add Account</button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {overview.accounts.map((acc) => (
                  <div
                    key={acc.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 14px',
                      background: 'var(--field)',
                      borderRadius: 6,
                      border: '1px solid var(--border)',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="badge badge-active">{acc.type.toUpperCase()}</span>
                        <strong style={{ fontSize: 14 }}>{acc.account_name}</strong>
                      </div>
                      <div className="muted mono" style={{ fontSize: 11, marginTop: 4 }}>
                        Code: {acc.account_code} {acc.bank_name ? `· ${acc.bank_name} (${acc.account_number || 'A/C'})` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="mono money" style={{ fontSize: 16, fontWeight: 700, color: acc.current_balance >= 0 ? 'var(--ink)' : 'var(--failed)' }}>
                        {formatMoney(acc.current_balance)}
                      </div>
                      <span className="muted" style={{ fontSize: 10, textTransform: 'uppercase' }}>Available</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Traceable Transactions */}
            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <span className="eyebrow">Ledger Activity</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Recent Financial Transactions</h3>
                </div>
                <button className="btn-quiet btn-sm" onClick={() => setTab('transactions')}>View All</button>
              </div>

              {overview.recentTransactions.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {overview.recentTransactions.map((tx) => {
                    const isCredit = tx.transaction_type === 'income' || tx.transaction_type === 'customer_receipt';
                    return (
                      <div
                        key={tx.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px 12px',
                          borderBottom: '1px solid var(--border)',
                          fontSize: 13,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600 }}>{tx.category}</div>
                          <div className="muted mono" style={{ fontSize: 11 }}>
                            {tx.transaction_date} · #{tx.transaction_number} {tx.description ? `· ${tx.description}` : ''}
                          </div>
                        </div>
                        <div className="mono money" style={{ fontSize: 14, fontWeight: 700, color: isCredit ? 'var(--verified)' : 'var(--ink)' }}>
                          {isCredit ? '+' : '-'}{formatMoney(tx.amount)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty" style={{ padding: 20 }}>No transactions recorded yet.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---------------- 2. GENERAL LEDGER TRANSACTIONS TAB ---------------- */}
      {tab === 'transactions' && (
        <div className="panel" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <span className="eyebrow">Traceable General Ledger</span>
              <h2 style={{ margin: '2px 0 0 0', fontSize: 18 }}>Financial Transactions Register</h2>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <select
                value={txTypeFilter}
                onChange={(e) => setTxTypeFilter(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
              >
                <option value="all">All Transaction Types</option>
                <option value="income">Income / Receipts</option>
                <option value="expense">Operating Expenses</option>
                <option value="payroll">Payroll Disbursal</option>
                <option value="vendor_payment">Vendor Payments</option>
                <option value="transfer">Inter-Account Transfers</option>
              </select>

              {can('finance.manage') && (
                <button className="btn-primary btn-sm" onClick={() => setShowTxModal(true)}>
                  + Record Transaction
                </button>
              )}
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Tx Number</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Account</th>
                  <th>Category / Purpose</th>
                  <th>Reference</th>
                  <th style={{ textAlign: 'right' }}>Amount ({jurisdiction.currencySymbol})</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx) => {
                  const acc = accounts.find((a) => a.id === tx.account_id);
                  const isCredit = tx.transaction_type === 'income' || tx.transaction_type === 'customer_receipt';
                  return (
                    <tr key={tx.id}>
                      <td className="mono" style={{ fontWeight: 600 }}>{tx.transaction_number}</td>
                      <td className="mono">{tx.transaction_date}</td>
                      <td>
                        <span className={`badge ${isCredit ? 'badge-active' : 'badge-neutral'}`}>
                          {tx.transaction_type.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <strong>{acc?.account_name || 'Account'}</strong>
                        <span className="muted mono" style={{ fontSize: 11, display: 'block' }}>{acc?.account_code}</span>
                      </td>
                      <td>
                        <strong>{tx.category}</strong>
                        {tx.description && <span className="muted" style={{ fontSize: 11, display: 'block' }}>{tx.description}</span>}
                      </td>
                      <td className="mono muted">{tx.reference_number || '—'}</td>
                      <td style={{ textAlign: 'right' }} className="mono money">
                        <strong style={{ color: isCredit ? 'var(--verified)' : 'var(--ink)' }}>
                          {isCredit ? '+' : '-'}{formatMoney(tx.amount)}
                        </strong>
                      </td>
                      <td><StatusBadge status={tx.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---------------- 3. BANK & CASH ACCOUNTS TAB ---------------- */}
      {tab === 'accounts' && (
        <div className="panel" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <span className="eyebrow">Treasury & Liquidity</span>
              <h2 style={{ margin: '2px 0 0 0', fontSize: 18 }}>Bank & Cash Accounts Master</h2>
            </div>
            {can('finance.manage') && (
              <button className="btn-primary btn-sm" onClick={() => setShowAccModal(true)}>
                + Add Account
              </button>
            )}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Account Code</th>
                  <th>Account Name</th>
                  <th>Type</th>
                  <th>Bank / Branch</th>
                  <th>Account Number / Routing</th>
                  <th style={{ textAlign: 'right' }}>Opening Balance</th>
                  <th style={{ textAlign: 'right' }}>Current Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((acc) => (
                  <tr key={acc.id}>
                    <td className="mono" style={{ fontWeight: 700 }}>{acc.account_code}</td>
                    <td><strong>{acc.account_name}</strong></td>
                    <td>
                      <span className="badge badge-neutral">{acc.type.toUpperCase()}</span>
                    </td>
                    <td>{acc.bank_name ? `${acc.bank_name} (${acc.branch_name || 'Main'})` : '—'}</td>
                    <td className="mono muted">
                      {acc.account_number ? `${acc.account_number} / ${acc.ifsc_code || 'N/A'}` : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }} className="mono money">{formatMoney(acc.opening_balance)}</td>
                    <td style={{ textAlign: 'right' }} className="mono money">
                      <strong style={{ fontSize: 14, color: acc.current_balance >= 0 ? 'var(--verified)' : 'var(--failed)' }}>
                        {formatMoney(acc.current_balance)}
                      </strong>
                    </td>
                    <td>
                      <span className={`badge ${acc.is_active ? 'badge-active' : 'badge-archived'}`}>
                        {acc.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}


      {/* ---------------- 4. PAYABLES (AP) & RECEIVABLES (AR) TAB ---------------- */}
      {tab === 'invoices' && (
        <div className="panel" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <span className="eyebrow">Accounts Payable & Accounts Receivable</span>
              <h2 style={{ margin: '2px 0 0 0', fontSize: 18 }}>Client Invoices & Vendor Bills</h2>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <select
                value={invoiceTypeFilter}
                onChange={(e) => setInvoiceTypeFilter(e.target.value as any)}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }}
              >
                <option value="all">All Documents (AP + AR)</option>
                <option value="customer_invoice">Customer Invoices (Receivables AR)</option>
                <option value="vendor_bill">Vendor Bills (Payables AP)</option>
              </select>

              {can('finance.manage') && (
                <>
                  <button
                    className="btn-primary btn-sm"
                    onClick={() => {
                      setInvForm({
                        ...invForm,
                        invoiceType: 'customer_invoice',
                        invoiceNumber: `INV-${new Date().getFullYear()}-${String(invoices.length + 1).padStart(3, '0')}`,
                      });
                      setShowInvoiceModal(true);
                    }}
                  >
                    + Raise Customer Invoice (AR)
                  </button>
                  <button
                    className="btn-quiet btn-sm"
                    onClick={() => {
                      setInvForm({
                        ...invForm,
                        invoiceType: 'vendor_bill',
                        invoiceNumber: `BILL-${new Date().getFullYear()}-${String(invoices.length + 1).padStart(3, '0')}`,
                      });
                      setShowInvoiceModal(true);
                    }}
                  >
                    📎 Record / Upload Vendor Bill (AP)
                  </button>
                </>
              )}
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Doc Number</th>
                  <th>Type</th>
                  <th>Partner / Entity</th>
                  <th>Issue Date</th>
                  <th>Due Date</th>
                  <th style={{ textAlign: 'right' }}>Total ({jurisdiction.currencySymbol})</th>
                  <th style={{ textAlign: 'right' }}>Paid ({jurisdiction.currencySymbol})</th>
                  <th style={{ textAlign: 'right' }}>Balance Due ({jurisdiction.currencySymbol})</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((inv) => {
                  const client = clients.find((c) => c.id === inv.client_id);
                  const vendor = vendors.find((v) => v.id === inv.vendor_id);
                  const balanceDue = inv.total_amount - (inv.paid_amount || 0);
                  const isAR = inv.invoice_type === 'customer_invoice';

                  return (
                    <tr key={inv.id}>
                      <td className="mono" style={{ fontWeight: 700 }}>
                        <span style={{ cursor: 'pointer', color: 'var(--brand-primary)' }} onClick={() => setViewInvoiceModal(inv)}>
                          {inv.invoice_number}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${isAR ? 'badge-active' : 'badge-neutral'}`}>
                          {isAR ? 'CLIENT INVOICE' : 'VENDOR BILL'}
                        </span>
                      </td>
                      <td>
                        <strong>{isAR ? client?.name || 'Client' : vendor?.legal_name || 'Vendor'}</strong>
                      </td>
                      <td className="mono">{inv.issue_date}</td>
                      <td className="mono">{inv.due_date}</td>
                      <td style={{ textAlign: 'right' }} className="mono money">{formatMoney(inv.total_amount)}</td>
                      <td style={{ textAlign: 'right' }} className="mono money muted">{formatMoney(inv.paid_amount || 0)}</td>
                      <td style={{ textAlign: 'right' }} className="mono money">
                        <strong style={{ color: balanceDue > 0 ? 'var(--status-pending)' : 'var(--status-verified)' }}>
                          {formatMoney(balanceDue)}
                        </strong>
                      </td>
                      <td><StatusBadge status={inv.status} /></td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            className="btn-quiet btn-sm"
                            onClick={() => setViewInvoiceModal(inv)}
                            title="View Printable Tax Invoice"
                          >
                            <Icon name="eye" size="xs" />
                            <span>View</span>
                          </button>
                          {balanceDue > 0 && can('finance.manage') && (
                            <button
                              className="btn-primary btn-sm"
                              onClick={() => {
                                setShowSettleModal(inv);
                                setSettleForm({
                                  amount: String(balanceDue),
                                  accountId: accounts[0]?.id || '',
                                  paymentReference: '',
                                });
                              }}
                            >
                              💳 Settle
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}


      {/* ---------------- 5. VENDORS & CLIENTS PARTNERS TAB ---------------- */}
      {tab === 'partners' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 20 }}>
          {/* Vendors Master */}
          <div className="panel" style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <span className="eyebrow">Accounts Payable</span>
                <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Approved Vendors & Suppliers</h3>
              </div>
              {can('finance.manage') && (
                <button className="btn-primary btn-sm" onClick={() => setShowVendorModal(true)}>
                  + Add Vendor
                </button>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {vendors.map((v) => (
                <div key={v.id} style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong>{v.legal_name}</strong>
                    <span className="muted mono" style={{ fontSize: 11 }}>Code: {v.vendor_code}</span>
                  </div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                    Category: {v.category || 'Vendor'} · Terms: Net {v.payment_terms_days} Days
                  </div>
                  <div className="muted mono" style={{ fontSize: 11 }}>
                    Tax ID: {v.gstin || v.pan || '—'} {v.contact_email ? `· ${v.contact_email}` : ''}
                  </div>
                </div>
              ))}
              {vendors.length === 0 && <div className="empty">No vendors configured.</div>}
            </div>
          </div>

          {/* Clients Master */}
          <div className="panel" style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <span className="eyebrow">Accounts Receivable</span>
                <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Corporate Clients & Customers</h3>
              </div>
              {can('finance.manage') && (
                <button className="btn-primary btn-sm" onClick={() => setShowClientModal(true)}>
                  + Add Client
                </button>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {clients.map((c) => (
                <div key={c.id} style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong>{c.name}</strong>
                    <span className="muted mono" style={{ fontSize: 11 }}>Code: {c.client_code}</span>
                  </div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                    Trade: {c.trade_name || 'Client'} · Terms: Net {c.payment_terms_days} Days
                  </div>
                  <div className="muted mono" style={{ fontSize: 11 }}>
                    Tax ID: {c.gstin || c.pan || '—'} {c.contact_email ? `· ${c.contact_email}` : ''}
                  </div>
                </div>
              ))}
              {clients.length === 0 && <div className="empty">No clients configured.</div>}
            </div>
          </div>

        </div>
      )}

      {/* ---------------- 6. BUDGETS & COST CENTERS TAB ---------------- */}
      {tab === 'budgets' && (
        <div className="panel" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <span className="eyebrow">Cost Governance</span>
              <h2 style={{ margin: '2px 0 0 0', fontSize: 18 }}>Departmental Budgets & Threshold Controls</h2>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <select
                value={fiscalYear}
                onChange={(e) => setFiscalYear(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
              >
                <option value="2026-2027">FY 2026-2027</option>
                <option value="2025-2026">FY 2025-2026</option>
              </select>

              {can('finance.manage') && (
                <button className="btn-primary btn-sm" onClick={() => setShowBudgetModal(true)}>
                  + Set Budget
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            {budgets.map((b) => {
              const utilPct = b.utilizationPercent;
              const dept = departments.find((d) => d.id === b.department_id);
              const proj = projects.find((p) => p.id === b.project_id);

              return (
                <div key={b.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, background: 'var(--bg-surface)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <strong style={{ fontSize: 15 }}>{dept ? dept.name : proj ? proj.name : 'General Operations'}</strong>
                      <span className="muted mono" style={{ fontSize: 11, display: 'block' }}>{b.category} · {b.fiscal_year}</span>
                    </div>
                    <span
                      className={`badge ${
                        b.alertStatus === 'exceeded' ? 'badge-failed' : b.alertStatus === 'warning' ? 'badge-pending' : 'badge-active'
                      }`}
                    >
                      {utilPct}% UTILIZED · {b.alertStatus.toUpperCase()}
                    </span>
                  </div>

                  <div style={{ height: 8, width: '100%', background: '#e0e0e0', borderRadius: 4, overflow: 'hidden', margin: '10px 0' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.min(100, utilPct)}%`,
                        background: utilPct >= 100 ? 'var(--failed)' : utilPct >= b.alert_threshold_percent ? 'var(--pending)' : 'var(--verified)',
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginTop: 12, fontSize: 12 }}>
                    <div>
                      <span className="muted" style={{ display: 'block', textTransform: 'uppercase', fontSize: 10 }}>Allocated Budget</span>
                      <strong className="mono money" style={{ fontSize: 14 }}>{formatMoney(b.allocated_amount)}</strong>
                    </div>
                    <div>
                      <span className="muted" style={{ display: 'block', textTransform: 'uppercase', fontSize: 10 }}>Utilized Cost</span>
                      <strong className="mono money" style={{ fontSize: 14 }}>{formatMoney(b.utilized_amount)}</strong>
                    </div>
                    <div>
                      <span className="muted" style={{ display: 'block', textTransform: 'uppercase', fontSize: 10 }}>Remaining Balance</span>
                      <strong className="mono money" style={{ fontSize: 14, color: b.remainingAmount >= 0 ? 'var(--verified)' : 'var(--failed)' }}>
                        {formatMoney(b.remainingAmount)}
                      </strong>
                    </div>
                    <div>
                      <span className="muted" style={{ display: 'block', textTransform: 'uppercase', fontSize: 10 }}>Alert Threshold</span>
                      <strong className="mono" style={{ fontSize: 14 }}>{b.alert_threshold_percent}%</strong>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------------- 7. MANAGEMENT P&L STATEMENT TAB ---------------- */}
      {tab === 'pl' && pl && (
        <div className="panel" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <span className="eyebrow">Financial Performance ({jurisdiction.accountingStandard})</span>
              <h2 style={{ margin: '2px 0 0 0', fontSize: 18 }}>Management Profit & Loss Statement</h2>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span className="muted" style={{ fontSize: 12 }}>Period:</span>
              <input
                type="date"
                value={plFromDate}
                onChange={(e) => setPlFromDate(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)', fontSize: 12 }}
              />
              <span className="muted">to</span>
              <input
                type="date"
                value={plToDate}
                onChange={(e) => setPlToDate(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)', fontSize: 12 }}
              />
            </div>
          </div>

          {/* P&L Metric Cards */}
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 20 }}>
            <div className="stat-card">
              <span className="stat-label">Operating Revenue</span>
              <span className="stat-value money" style={{ color: 'var(--verified)' }}>
                {formatMoney(pl.revenue)}
              </span>
              <span className="stat-hint">100% of Top-line</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Gross Margin</span>
              <span className="stat-value money" style={{ color: 'var(--ink)' }}>
                {formatMoney(pl.grossProfit)}
              </span>
              <span className="stat-hint">{pl.grossMarginPercent}% gross margin</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Operating Expenses (OPEX)</span>
              <span className="stat-value money" style={{ color: 'var(--pending)' }}>
                {formatMoney(pl.operatingCosts)}
              </span>
              <span className="stat-hint">Salaries, Rent, Cloud, Admin</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Net Operating Profit</span>
              <span className="stat-value money" style={{ color: pl.netOperatingProfit >= 0 ? 'var(--verified)' : 'var(--failed)' }}>
                {formatMoney(pl.netOperatingProfit)}
              </span>
              <span className="stat-hint">{pl.netMarginPercent}% net margin</span>
            </div>
          </div>

          {/* Breakdown Tables */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
            <div style={{ background: 'var(--field)', padding: 16, borderRadius: 6 }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: 14 }}>Revenue Breakdown</h4>
              {Object.entries(pl.breakdown.revenueByCat).map(([cat, amt]) => (
                <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span>{cat}</span>
                  <strong className="mono money">{formatMoney(amt)}</strong>
                </div>
              ))}
              {Object.keys(pl.breakdown.revenueByCat).length === 0 && <div className="muted" style={{ fontSize: 12 }}>No revenue recorded in period.</div>}
            </div>

            <div style={{ background: 'var(--field)', padding: 16, borderRadius: 6 }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: 14 }}>Operating Expenses Breakdown</h4>
              {Object.entries(pl.breakdown.operatingCostsByCat).map(([cat, amt]) => (
                <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span>{cat}</span>
                  <strong className="mono money">{formatMoney(amt)}</strong>
                </div>
              ))}
              {Object.keys(pl.breakdown.operatingCostsByCat).length === 0 && <div className="muted" style={{ fontSize: 12 }}>No expenses recorded in period.</div>}
            </div>
          </div>
        </div>
      )}

      {/* ---------------- MODALS ---------------- */}


      {/* 1. Record Transaction Modal */}
      {showTxModal && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3>Record Financial Transaction</h3>
              <button className="btn-quiet btn-sm" onClick={() => setShowTxModal(false)} aria-label="Close">
                <Icon name="x" size="xs" />
              </button>
            </div>
            <form onSubmit={(e) => void handleRecordTx(e)}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="form-group">
                  <label>Transaction Type *</label>
                  <select
                    value={txForm.transactionType}
                    onChange={(e) => setTxForm({ ...txForm, transactionType: e.target.value as any })}
                    required
                  >
                    <option value="expense">Operating Expense</option>
                    <option value="income">Direct Income / Receipt</option>
                    <option value="vendor_payment">Vendor Payment</option>
                    <option value="customer_receipt">Customer Invoice Receipt</option>
                    <option value="transfer">Account Transfer</option>
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>Date *</label>
                    <input
                      type="date"
                      value={txForm.transactionDate}
                      onChange={(e) => setTxForm({ ...txForm, transactionDate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                  <label>Amount ({jurisdiction.currencySymbol}) *</label>
                  <input
                    type="number"
                    placeholder="e.g. 50000"
                    value={txForm.amount}
                    onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })}
                    required
                  />
                </div>
                </div>

                <div className="form-group">
                  <label>Account *</label>
                  <select
                    value={txForm.accountId}
                    onChange={(e) => setTxForm({ ...txForm, accountId: e.target.value })}
                    required
                  >
                    <option value="">Select Financial Account</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.account_name} ({a.account_code}) — Bal: {formatMoney(a.current_balance)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <input
                    type="text"
                    placeholder="e.g. Cloud Hosting, Office Supplies, Client Retainer"
                    value={txForm.category}
                    onChange={(e) => setTxForm({ ...txForm, category: e.target.value })}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>Department (Optional)</label>
                    <select
                      value={txForm.departmentId}
                      onChange={(e) => setTxForm({ ...txForm, departmentId: e.target.value })}
                    >
                      <option value="">None / Corporate</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>{d.name} ({d.code})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Reference #</label>
                    <input
                      type="text"
                      placeholder="UTR / Check / Ref #"
                      value={txForm.referenceNumber}
                      onChange={(e) => setTxForm({ ...txForm, referenceNumber: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description / Memo</label>
                  <input
                    type="text"
                    placeholder="Detailed explanation of the transaction"
                    value={txForm.description}
                    onChange={(e) => setTxForm({ ...txForm, description: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button type="button" className="btn-quiet" onClick={() => setShowTxModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Record Transaction</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. Add Account Modal */}
      {showAccModal && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h3>+ Add Financial Account</h3>
              <button className="btn-quiet btn-sm" onClick={() => setShowAccModal(false)}>✕</button>
            </div>
            <form onSubmit={(e) => void handleCreateAccount(e)}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>Account Code *</label>
                    <input
                      type="text"
                      placeholder="e.g. 1010"
                      value={accForm.accountCode}
                      onChange={(e) => setAccForm({ ...accForm, accountCode: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Account Type *</label>
                    <select
                      value={accForm.type}
                      onChange={(e) => setAccForm({ ...accForm, type: e.target.value as any })}
                      required
                    >
                      <option value="bank">Bank Account</option>
                      <option value="cash">Petty Cash</option>
                      <option value="escrow">Escrow</option>
                      <option value="clearing">Clearing</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Account Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. Chase Main Operating A/C"
                    value={accForm.accountName}
                    onChange={(e) => setAccForm({ ...accForm, accountName: e.target.value })}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>Bank Name</label>
                    <input
                      type="text"
                      placeholder="e.g. JPMorgan Chase"
                      value={accForm.bankName}
                      onChange={(e) => setAccForm({ ...accForm, bankName: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Account / Routing #</label>
                    <input
                      type="text"
                      placeholder="e.g. 987654321"
                      value={accForm.accountNumber}
                      onChange={(e) => setAccForm({ ...accForm, accountNumber: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Opening Balance ({jurisdiction.currencySymbol})</label>
                  <input
                    type="number"
                    value={accForm.openingBalance}
                    onChange={(e) => setAccForm({ ...accForm, openingBalance: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button type="button" className="btn-quiet" onClick={() => setShowAccModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Create Account</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. Settle Payment Modal */}
      {showSettleModal && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3>Settle Invoice / Bill Payment</h3>
              <button className="btn-quiet btn-sm" onClick={() => setShowSettleModal(null)} aria-label="Close">
                <Icon name="x" size="xs" />
              </button>
            </div>
            <form onSubmit={(e) => void handleSettleInvoice(e)}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ padding: 10, background: 'var(--field)', borderRadius: 6, fontSize: 13 }}>
                  <div>Doc: <strong>#{showSettleModal.invoice_number}</strong> ({showSettleModal.invoice_type.replace('_', ' ').toUpperCase()})</div>
                  <div>Total Amount: <strong>{formatMoney(showSettleModal.total_amount)}</strong> · Already Paid: <strong>{formatMoney(showSettleModal.paid_amount || 0)}</strong></div>
                </div>

                <div className="form-group">
                  <label>Payment Amount ({jurisdiction.currencySymbol}) *</label>
                  <input
                    type="number"
                    value={settleForm.amount}
                    onChange={(e) => setSettleForm({ ...settleForm, amount: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Paid Through Account *</label>
                  <select
                    value={settleForm.accountId}
                    onChange={(e) => setSettleForm({ ...settleForm, accountId: e.target.value })}
                    required
                  >
                    <option value="">Select Account</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>{a.account_name} ({formatMoney(a.current_balance)})</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Payment Reference / UTR #</label>
                  <input
                    type="text"
                    placeholder="e.g. ACH-99887766"
                    value={settleForm.paymentReference}
                    onChange={(e) => setSettleForm({ ...settleForm, paymentReference: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button type="button" className="btn-quiet" onClick={() => setShowSettleModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Confirm Settlement</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. Issue Invoice / Bill Modal with Document Upload */}
      {showInvoiceModal && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: 540 }}>
            <div className="modal-header">
              <h3>
                {invForm.invoiceType === 'customer_invoice'
                  ? 'Issue Customer Invoice (AR)'
                  : 'Record Vendor Bill (AP)'}
              </h3>
              <button className="btn-quiet btn-sm" onClick={() => { setShowInvoiceModal(false); setAttachedDocName(null); }} aria-label="Close">
                <Icon name="x" size="xs" />
              </button>
            </div>
            <form onSubmit={(e) => void handleCreateInvoice(e)}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>Document # *</label>
                    <input
                      type="text"
                      placeholder="e.g. INV-2026-002"
                      value={invForm.invoiceNumber}
                      onChange={(e) => setInvForm({ ...invForm, invoiceNumber: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{invForm.invoiceType === 'customer_invoice' ? 'Client *' : 'Vendor *'}</label>
                    {invForm.invoiceType === 'customer_invoice' ? (
                      <select
                        value={invForm.clientId}
                        onChange={(e) => setInvForm({ ...invForm, clientId: e.target.value })}
                        required
                      >
                        <option value="">Select Client</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.id}>{c.name} ({c.client_code})</option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={invForm.vendorId}
                        onChange={(e) => setInvForm({ ...invForm, vendorId: e.target.value })}
                        required
                      >
                        <option value="">Select Vendor</option>
                        {vendors.map((v) => (
                          <option key={v.id} value={v.id}>{v.legal_name} ({v.vendor_code})</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>Issue Date *</label>
                    <input
                      type="date"
                      value={invForm.issueDate}
                      onChange={(e) => setInvForm({ ...invForm, issueDate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Due Date *</label>
                    <input
                      type="date"
                      value={invForm.dueDate}
                      onChange={(e) => setInvForm({ ...invForm, dueDate: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>Subtotal ({jurisdiction.currencySymbol}) *</label>
                    <input
                      type="number"
                      placeholder="e.g. 100000"
                      value={invForm.subtotal}
                      onChange={(e) => {
                        const val = e.target.value;
                        const sub = Number(val || 0);
                        const taxRate = country === 'US' ? 0.08 : country === 'IN' ? 0.18 : country === 'GB' ? 0.20 : country === 'AE' ? 0.05 : 0.09;
                        const tax = Math.round(sub * taxRate);
                        setInvForm({ ...invForm, subtotal: val, taxAmount: String(tax) });
                      }}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <label>{jurisdiction.invoiceTaxLabel} ({jurisdiction.currencySymbol})</label>
                      <span className="muted" style={{ fontSize: 10 }}>({jurisdiction.taxIdLabel})</span>
                    </div>
                    <input
                      type="number"
                      placeholder="e.g. 18000"
                      value={invForm.taxAmount}
                      onChange={(e) => setInvForm({ ...invForm, taxAmount: e.target.value })}
                    />
                  </div>
                </div>

                {invForm.subtotal && (
                  <div style={{ padding: '8px 12px', background: 'var(--bg-field)', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12 }}>Gross Total Amount:</span>
                    <strong className="mono money" style={{ fontSize: 16, color: 'var(--brand-primary)' }}>
                      {formatMoney(Number(invForm.subtotal || 0) + Number(invForm.taxAmount || 0))}
                    </strong>
                  </div>
                )}

                <div className="form-group">
                  <label>Attach / Upload Invoice Document (PDF / Scan)</label>
                  <div style={{ border: '2px dashed var(--border-subtle)', borderRadius: 6, padding: '14px', textAlign: 'center', background: 'var(--bg-field)' }}>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      id="invoiceFileInput"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) {
                          setAttachedDocName(f.name);
                        }
                      }}
                    />
                    <label htmlFor="invoiceFileInput" style={{ cursor: 'pointer', margin: 0, textTransform: 'none' }}>
                      {attachedDocName ? (
                        <div style={{ color: 'var(--status-verified)', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <Icon name="fileText" size="sm" color="var(--status-verified)" />
                          <span>Attached: {attachedDocName}</span>
                          <span style={{ fontSize: 11, color: 'var(--ink-muted)' }}>(Click to replace)</span>
                        </div>
                      ) : (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                            <Icon name="folder" size="xl" color="var(--brand-primary)" />
                          </div>
                          <span style={{ fontSize: 12, color: 'var(--brand-primary)', fontWeight: 600 }}>Click to Upload Document / PDF</span>
                          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>Supports PDF, PNG, JPG up to 10MB</div>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes / Scope of Work</label>
                  <input
                    type="text"
                    placeholder="Milestone deliverables or scope description"
                    value={invForm.notes}
                    onChange={(e) => setInvForm({ ...invForm, notes: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button type="button" className="btn-quiet" onClick={() => { setShowInvoiceModal(false); setAttachedDocName(null); }}>Cancel</button>
                <button type="submit" className="btn-primary">
                  {invForm.invoiceType === 'customer_invoice' ? 'Issue Customer Invoice' : 'Save & Upload Bill'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4B. View Printable Invoice / Document Preview Modal */}
      {viewInvoiceModal && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="fileText" size="lg" color="var(--brand-primary)" />
                <div>
                  <h3 style={{ margin: 0 }}>Tax Document #{viewInvoiceModal.invoice_number}</h3>
                  <span className="muted" style={{ fontSize: 11 }}>
                    {viewInvoiceModal.invoice_type === 'customer_invoice' ? 'Commercial Customer Invoice (AR)' : 'Vendor Procurement Bill (AP)'}
                  </span>
                </div>
              </div>
              <button className="btn-quiet btn-sm" onClick={() => setViewInvoiceModal(null)} aria-label="Close">
                <Icon name="x" size="xs" />
              </button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 12, borderBottom: '1px solid var(--border-subtle)' }}>
                <div>
                  <div className="eyebrow" style={{ color: 'var(--brand-primary)' }}>Issued By</div>
                  <strong>{session?.tenant.name || jurisdiction.sampleOrgName}</strong>
                  <div className="muted" style={{ fontSize: 12 }}>{jurisdiction.taxIdLabel}: <strong>{jurisdiction.taxIdPlaceholder}</strong></div>
                  <div className="muted" style={{ fontSize: 12 }}>{jurisdiction.sampleAddress}</div>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <StatusBadge status={viewInvoiceModal.status} />
                  <div className="mono" style={{ fontSize: 12, marginTop: 6 }}>
                    Issue Date: <strong>{viewInvoiceModal.issue_date}</strong>
                  </div>
                  <div className="mono" style={{ fontSize: 12 }}>
                    Due Date: <strong>{viewInvoiceModal.due_date}</strong>
                  </div>
                </div>
              </div>

              <div style={{ padding: '12px 14px', background: 'var(--bg-field)', borderRadius: 6 }}>
                <div className="eyebrow">
                  {viewInvoiceModal.invoice_type === 'customer_invoice' ? 'Billed To (Customer)' : 'Payable To (Vendor)'}
                </div>
                {viewInvoiceModal.invoice_type === 'customer_invoice' ? (
                  <div>
                    <strong style={{ fontSize: 15 }}>{clients.find((c) => c.id === viewInvoiceModal.client_id)?.name || 'Client Organization'}</strong>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {jurisdiction.taxIdLabel}: {clients.find((c) => c.id === viewInvoiceModal.client_id)?.gstin || clients.find((c) => c.id === viewInvoiceModal.client_id)?.pan || jurisdiction.taxIdPlaceholder}
                    </div>
                  </div>
                ) : (
                  <div>
                    <strong style={{ fontSize: 15 }}>{vendors.find((v) => v.id === viewInvoiceModal.vendor_id)?.legal_name || 'Vendor Payee'}</strong>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {jurisdiction.taxIdLabel}: {vendors.find((v) => v.id === viewInvoiceModal.vendor_id)?.gstin || vendors.find((v) => v.id === viewInvoiceModal.vendor_id)?.pan || jurisdiction.taxIdPlaceholder}
                    </div>
                  </div>
                )}

              </div>

              <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 6, overflow: 'hidden' }}>
                <table className="table" style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th style={{ textAlign: 'right' }}>Subtotal</th>
                      <th style={{ textAlign: 'right' }}>Tax ({jurisdiction.invoiceTaxLabel})</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>{viewInvoiceModal.notes || 'Professional SaaS Software & Managed Services'}</strong>
                        <span className="muted" style={{ display: 'block', fontSize: 11 }}>Code: 998314</span>
                      </td>
                      <td style={{ textAlign: 'right' }} className="mono money">{formatMoney(viewInvoiceModal.subtotal)}</td>
                      <td style={{ textAlign: 'right' }} className="mono money">{formatMoney(viewInvoiceModal.tax_amount || 0)}</td>
                      <td style={{ textAlign: 'right' }} className="mono money">
                        <strong>{formatMoney(viewInvoiceModal.total_amount)}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
                <div>
                  <span className="muted" style={{ fontSize: 12 }}>Settlement Status:</span>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    Paid: <span className="mono" style={{ color: 'var(--status-verified)' }}>{formatMoney(viewInvoiceModal.paid_amount || 0)}</span> · Balance Due: <span className="mono" style={{ color: 'var(--status-pending)' }}>{formatMoney(viewInvoiceModal.total_amount - (viewInvoiceModal.paid_amount || 0))}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="btn-quiet btn-sm"
                    onClick={() => {
                      alert(`Printing tax invoice #${viewInvoiceModal.invoice_number}`);
                    }}
                  >
                    <Icon name="printer" size="xs" />
                    <span>Print / PDF</span>
                  </button>
                  {viewInvoiceModal.total_amount > (viewInvoiceModal.paid_amount || 0) && can('finance.manage') && (
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      onClick={() => {
                        const inv = viewInvoiceModal;
                        setViewInvoiceModal(null);
                        setShowSettleModal(inv);
                        setSettleForm({
                          amount: String(inv.total_amount - (inv.paid_amount || 0)),
                          accountId: accounts[0]?.id || '',
                          paymentReference: '',
                        });
                      }}
                    >
                      💳 Settle Payment
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-quiet" onClick={() => setViewInvoiceModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}


      {/* 5. Set Budget Modal */}
      {showBudgetModal && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h3>+ Set Cost Center / Department Budget</h3>
              <button className="btn-quiet btn-sm" onClick={() => setShowBudgetModal(false)}>✕</button>
            </div>
            <form onSubmit={(e) => void handleSetBudget(e)}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>Fiscal Year *</label>
                    <input
                      type="text"
                      value={budgetForm.fiscalYear}
                      onChange={(e) => setBudgetForm({ ...budgetForm, fiscalYear: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      value={budgetForm.category}
                      onChange={(e) => setBudgetForm({ ...budgetForm, category: e.target.value })}
                    >
                      <option value="operational">Operational (OPEX)</option>
                      <option value="capital">Capital (CAPEX)</option>
                      <option value="project">Project Direct</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Department (Optional)</label>
                  <select
                    value={budgetForm.departmentId}
                    onChange={(e) => setBudgetForm({ ...budgetForm, departmentId: e.target.value })}
                  >
                    <option value="">Corporate / All Departments</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{d.name} ({d.code})</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>Allocated Amount ({jurisdiction.currencySymbol}) *</label>
                    <input
                      type="number"
                      placeholder="e.g. 2500000"
                      value={budgetForm.allocatedAmount}
                      onChange={(e) => setBudgetForm({ ...budgetForm, allocatedAmount: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Alert Threshold %</label>
                    <input
                      type="number"
                      placeholder="80"
                      value={budgetForm.alertThresholdPercent}
                      onChange={(e) => setBudgetForm({ ...budgetForm, alertThresholdPercent: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button type="button" className="btn-quiet" onClick={() => setShowBudgetModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Budget</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. Add Vendor Modal */}
      {showVendorModal && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>+ Register New Vendor</h3>
              <button className="btn-quiet btn-sm" onClick={() => setShowVendorModal(false)}>✕</button>
            </div>
            <form onSubmit={(e) => void handleCreateVendor(e)}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>Vendor Code *</label>
                    <input
                      type="text"
                      placeholder="e.g. VND-002"
                      value={vendorForm.vendorCode}
                      onChange={(e) => setVendorForm({ ...vendorForm, vendorCode: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      value={vendorForm.category}
                      onChange={(e) => setVendorForm({ ...vendorForm, category: e.target.value })}
                    >
                      <option value="software">Software & SaaS</option>
                      <option value="hardware">Hardware & IT</option>
                      <option value="contractor">Contractor & Staffing</option>
                      <option value="utilities">Office & Utilities</option>
                      <option value="travel">Travel & Logistics</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Legal Entity Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. Dell Technologies India Pvt Ltd"
                    value={vendorForm.legalName}
                    onChange={(e) => setVendorForm({ ...vendorForm, legalName: e.target.value })}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>GSTIN</label>
                    <input
                      type="text"
                      placeholder="07AAACA..."
                      value={vendorForm.gstin}
                      onChange={(e) => setVendorForm({ ...vendorForm, gstin: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>PAN</label>
                    <input
                      type="text"
                      placeholder="AAACA..."
                      value={vendorForm.pan}
                      onChange={(e) => setVendorForm({ ...vendorForm, pan: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>Bank Name</label>
                    <input
                      type="text"
                      placeholder="e.g. ICICI Bank"
                      value={vendorForm.bankName}
                      onChange={(e) => setVendorForm({ ...vendorForm, bankName: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Account Number</label>
                    <input
                      type="text"
                      placeholder="000405..."
                      value={vendorForm.bankAccountNumber}
                      onChange={(e) => setVendorForm({ ...vendorForm, bankAccountNumber: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button type="button" className="btn-quiet" onClick={() => setShowVendorModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Register Vendor</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 7. Add Client Modal */}
      {showClientModal && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>+ Register New Client</h3>
              <button className="btn-quiet btn-sm" onClick={() => setShowClientModal(false)}>✕</button>
            </div>
            <form onSubmit={(e) => void handleCreateClient(e)}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>Client Code *</label>
                    <input
                      type="text"
                      placeholder="e.g. CLI-002"
                      value={clientForm.clientCode}
                      onChange={(e) => setClientForm({ ...clientForm, clientCode: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Terms (Days)</label>
                    <input
                      type="number"
                      placeholder="30"
                      value={clientForm.paymentTermsDays}
                      onChange={(e) => setClientForm({ ...clientForm, paymentTermsDays: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Client Organization Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. Acme Corporation"
                    value={clientForm.name}
                    onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>GSTIN</label>
                    <input
                      type="text"
                      placeholder="27AABCG..."
                      value={clientForm.gstin}
                      onChange={(e) => setClientForm({ ...clientForm, gstin: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Contact Email</label>
                    <input
                      type="email"
                      placeholder="billing@acme.test"
                      value={clientForm.contactEmail}
                      onChange={(e) => setClientForm({ ...clientForm, contactEmail: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Billing Address</label>
                  <input
                    type="text"
                    placeholder="Office address for invoice delivery"
                    value={clientForm.billingAddress}
                    onChange={(e) => setClientForm({ ...clientForm, billingAddress: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button type="button" className="btn-quiet" onClick={() => setShowClientModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Register Client</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
